import type { GameState, QuizState } from '../types/quiz.types';
import type { ProgressoPersistido, SessaoPersistida } from '../types/progresso.types';
import { PROGRESSO_STORAGE_VERSION, SESSION_PERSIST_STATUSES } from '../types/progresso.types';
import { getCicloPorFase } from './quizUtils';

export const PROGRESSO_STORAGE_KEY = 'quiz_imortais_progresso_v1';

const EMPTY_INVENTARIO: QuizState['inventario'] = {
  axe_dos_deuses: 0,
  ubuntu: 0,
  conselho_grios: 0,
  caminho_mandela: 0,
  escudo_palmares: 0,
};

const SESSION_STATUSES = new Set<GameState>(SESSION_PERSIST_STATUSES);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidInventario(value: unknown): value is QuizState['inventario'] {
  if (!isRecord(value)) return false;
  return (
    typeof value.axe_dos_deuses === 'number' &&
    typeof value.ubuntu === 'number' &&
    typeof value.conselho_grios === 'number' &&
    typeof value.caminho_mandela === 'number' &&
    typeof value.escudo_palmares === 'number'
  );
}

function isValidSessao(value: unknown): value is SessaoPersistida {
  if (!isRecord(value)) return false;
  if (typeof value.faseAtual !== 'number') return false;
  if (typeof value.status !== 'string' || !SESSION_STATUSES.has(value.status as GameState)) return false;
  if (typeof value.indicePerguntaAtual !== 'number') return false;
  if (typeof value.checkpointAtual !== 'number') return false;
  if (typeof value.vidas !== 'number') return false;
  if (typeof value.comboAtual !== 'number') return false;
  if (typeof value.pontuacaoFase !== 'number') return false;
  if (typeof value.ajudasUsadasNestaFase !== 'number') return false;
  if (!Array.isArray(value.desafiosConcluidosNestaJornada)) return false;
  if (typeof value.tempoRestantePergunta !== 'number') return false;
  if (typeof value.dueloAtivo !== 'boolean') return false;
  return true;
}

function normalizeLegacyProgresso(value: Record<string, unknown>): ProgressoPersistido | null {
  if (value.version !== 1 && value.version !== PROGRESSO_STORAGE_VERSION) return null;
  if (typeof value.moedas !== 'number') return null;
  if (typeof value.vidas !== 'number') return null;
  if (value.ultimaVidaPerdida !== null && typeof value.ultimaVidaPerdida !== 'number') return null;
  if (!isValidInventario(value.inventario)) return null;
  if (typeof value.faseAtual !== 'number') return null;
  if (!Array.isArray(value.fasesConcluidas)) return null;
  if (typeof value.tutorialConcluido !== 'boolean') return null;
  if (!Array.isArray(value.conquistasDesbloqueadas)) return null;
  if (value.ultimoBonusDiario !== null && typeof value.ultimoBonusDiario !== 'number') return null;
  if (!isRecord(value.historicoMetricas)) return null;

  const sessaoRaw = value.sessao;
  const sessao =
    sessaoRaw === null || sessaoRaw === undefined
      ? null
      : isValidSessao(sessaoRaw)
        ? sessaoRaw
        : null;

  return {
    version: PROGRESSO_STORAGE_VERSION,
    moedas: value.moedas,
    vidas: value.vidas,
    ultimaVidaPerdida: value.ultimaVidaPerdida as number | null,
    inventario: value.inventario,
    faseAtual: value.faseAtual,
    fasesConcluidas: value.fasesConcluidas as number[],
    tutorialConcluido: value.tutorialConcluido,
    conquistasDesbloqueadas: value.conquistasDesbloqueadas as string[],
    ultimoBonusDiario: value.ultimoBonusDiario as number | null,
    historicoMetricas: value.historicoMetricas as Record<number, QuizState['historicoMetricas'][number]>,
    sessao,
  };
}

function buildSessao(state: QuizState): SessaoPersistida | null {
  if (!SESSION_STATUSES.has(state.status)) return null;

  return {
    faseAtual: state.faseAtual,
    status: state.status === 'ERROR_PORTAL' || state.status === 'MINIGAME' ? 'PLAYING' : state.status,
    indicePerguntaAtual: state.indicePerguntaAtual,
    checkpointAtual: state.checkpointAtual,
    vidas: state.vidas,
    comboAtual: state.comboAtual,
    pontuacaoFase: state.pontuacaoFase,
    ajudasUsadasNestaFase: state.ajudasUsadasNestaFase,
    desafiosConcluidosNestaJornada: [...state.desafiosConcluidosNestaJornada],
    tempoRestantePergunta: state.tempoRestantePergunta,
    dueloAtivo: state.dueloAtivo,
  };
}

export function extractPersistable(state: QuizState): ProgressoPersistido {
  return {
    version: PROGRESSO_STORAGE_VERSION,
    moedas: state.moedas,
    vidas: state.vidas,
    ultimaVidaPerdida: state.ultimaVidaPerdida,
    inventario: { ...state.inventario },
    faseAtual: state.faseAtual,
    fasesConcluidas: [...state.fasesConcluidas],
    tutorialConcluido: state.tutorialConcluido,
    conquistasDesbloqueadas: [...state.conquistasDesbloqueadas],
    ultimoBonusDiario: state.ultimoBonusDiario,
    historicoMetricas: { ...state.historicoMetricas },
    sessao: buildSessao(state),
  };
}

export function loadProgresso(): ProgressoPersistido | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;

  try {
    const raw = window.localStorage.getItem(PROGRESSO_STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;

    const normalized = normalizeLegacyProgresso(parsed);
    if (!normalized) {
      console.warn('[progressStorage] Progresso salvo inválido ou versão incompatível; ignorando.');
      return null;
    }

    return normalized;
  } catch (error) {
    console.warn('[progressStorage] Falha ao carregar progresso:', error);
    return null;
  }
}

export function saveProgresso(data: ProgressoPersistido): void {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    window.localStorage.setItem(PROGRESSO_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('[progressStorage] Falha ao salvar progresso:', error);
  }
}

export function clearProgresso(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(PROGRESSO_STORAGE_KEY);
}

export function mergeIntoInitial(persisted: ProgressoPersistido | null, initialState: QuizState): QuizState {
  if (!persisted) return initialState;

  const sessao = persisted.sessao;
  const hasActiveSession = sessao && SESSION_STATUSES.has(sessao.status);
  const restoredStatus = hasActiveSession
    ? (sessao.status === 'ERROR_PORTAL' || sessao.status === 'MINIGAME' ? 'PLAYING' : sessao.status)
    : 'FASE_SELECT';

  return {
    ...initialState,
    moedas: persisted.moedas,
    vidas: hasActiveSession ? (sessao.vidas > 0 ? sessao.vidas : 5) : persisted.vidas,
    ultimaVidaPerdida: persisted.ultimaVidaPerdida,
    inventario: { ...EMPTY_INVENTARIO, ...persisted.inventario },
    faseAtual: hasActiveSession ? sessao.faseAtual : persisted.faseAtual,
    cicloAtual: getCicloPorFase(hasActiveSession ? sessao.faseAtual : persisted.faseAtual),
    fasesConcluidas: [...persisted.fasesConcluidas],
    tutorialConcluido: persisted.tutorialConcluido,
    conquistasDesbloqueadas: [...persisted.conquistasDesbloqueadas],
    ultimoBonusDiario: persisted.ultimoBonusDiario,
    historicoMetricas: { ...persisted.historicoMetricas },
    status: restoredStatus,
    indicePerguntaAtual: hasActiveSession ? sessao.indicePerguntaAtual : 0,
    checkpointAtual: hasActiveSession ? sessao.checkpointAtual : 0,
    comboAtual: hasActiveSession ? sessao.comboAtual : 0,
    pontuacaoFase: hasActiveSession ? sessao.pontuacaoFase : 0,
    ajudasUsadasNestaFase: hasActiveSession ? sessao.ajudasUsadasNestaFase : 0,
    desafiosConcluidosNestaJornada: hasActiveSession ? [...sessao.desafiosConcluidosNestaJornada] : [],
    tempoRestantePergunta: hasActiveSession ? (sessao.tempoRestantePergunta > 0 ? sessao.tempoRestantePergunta : 30) : 0,
    dueloAtivo: hasActiveSession ? sessao.dueloAtivo : false,
    statusAntesDaTenda: null,
    tempoSalvoAntesDaTenda: null,
  };
}

export function needsSessionHydration(persisted: ProgressoPersistido | null): boolean {
  return Boolean(persisted?.sessao && SESSION_STATUSES.has(persisted.sessao.status));
}
